import { EMPTY_DATE_PROXIMITY, type FormValues } from "@/widgets/classic-form/form";
import { locationScopeToDataModel } from "@/features/place-exactness";
import type { SearchDataModel } from "@/entities/data-model";

export interface MapFormEventsContext {
    /** Experiment-driven default year proximity (10 for test arm, null for control). */
    defaultYearProximity?: number | null;
    /** Whether Match All Terms Exactly is currently checked. */
    matchAllTermsExactly?: boolean;
}

export function mapFormEventsToDataModel(formValues: FormValues, context: MapFormEventsContext = {}) {
    const { defaultYearProximity = null, matchAllTermsExactly = false } = context;
    const mappedEvents: Partial<SearchDataModel["events"]> = {};

    if (formValues.events) {
        Object.entries(formValues.events).forEach(([eventKey, event]) => {
            mappedEvents[eventKey] = {
                ...event,
                relation: event.relation ?? "",
                eventName: event.eventName ?? "",
                instances: event.instances.map(({ isRangeMode, ...instance }) => {
                    const locationExactness = locationScopeToDataModel(instance.locationExactness);
                    let dateProximity = instance.dateProximity;
                    let date = instance.date;
                    if (isRangeMode) {
                        date = structuredClone(date);
                        date.month = null;
                        date.day = null;

                        const hasOnlyOneYear =
                            (typeof date.year === "number" || typeof date.endYear === "number") &&
                            !(typeof date.year === "number" && typeof date.endYear === "number");

                        if (hasOnlyOneYear && matchAllTermsExactly) {
                            // MATE selected: force Exact for both test and control arms
                            dateProximity = { year: 0, month: 0, day: 0 };
                        } else if (hasOnlyOneYear && defaultYearProximity !== null) {
                            // Experiment test arm: force ±10 regardless of prior selection
                            dateProximity = { year: defaultYearProximity, month: 0, day: 0 };
                        } else if (!hasOnlyOneYear) {
                            // Both years filled (true range): clear proximity
                            dateProximity = EMPTY_DATE_PROXIMITY;
                        }
                        // Control arm with single year: pass through existing proximity unchanged
                    } else {
                        // Case 12: proximity is meaningless without a year — strip it so it
                        // does not appear in the outbound URL. Ships to both arms.
                        if (typeof date.year !== "number") {
                            dateProximity = EMPTY_DATE_PROXIMITY;
                        }
                    }
                    return {
                        ...instance,
                        date: {
                            year: date.year,
                            month: date.month,
                            day: date.day,
                            endYear: date.endYear ?? null,
                        },
                        dateProximity,
                        locationExactness,
                    };
                }),
            };
        });
    }

    return mappedEvents;
}
